import { ITokensResponse } from './auth'
import { ISellerNotificationTopics } from './seller'

// Разделы кабинета, которые владелец открывает сотруднику. «Основная информация»,
// «Уведомления» и «Поддержка» доступны всегда, «Сотрудники» — только владельцу
export enum ESellerPermission {
  GOODS = 'GOODS',
  ORDERS = 'ORDERS',
  CHATS = 'CHATS',
  SHOPS = 'SHOPS',
  DELIVERY = 'DELIVERY',
  ACCOUNTING = 'ACCOUNTING',
  MASS_IMPORT = 'MASS_IMPORT',
  API_SYNC = 'API_SYNC',
  PROMOCODES = 'PROMOCODES',
  DISCOUNTS = 'DISCOUNTS',
  REVIEWS = 'REVIEWS'
}

export const SELLER_PERMISSION_LABELS: Record<ESellerPermission, string> = {
  [ESellerPermission.GOODS]: 'Товары',
  [ESellerPermission.ORDERS]: 'Заказы',
  [ESellerPermission.CHATS]: 'Сообщения',
  [ESellerPermission.SHOPS]: 'Адреса магазинов',
  [ESellerPermission.DELIVERY]: 'Способы доставки',
  [ESellerPermission.ACCOUNTING]: 'Бухгалтерия',
  [ESellerPermission.MASS_IMPORT]: 'Массовый импорт',
  [ESellerPermission.API_SYNC]: 'API синхронизация',
  [ESellerPermission.PROMOCODES]: 'Промокоды',
  [ESellerPermission.DISCOUNTS]: 'Скидки',
  [ESellerPermission.REVIEWS]: 'Отзывы'
}

export const ALL_SELLER_PERMISSIONS = Object.values(ESellerPermission)

export enum ESellerEmployeeStatus {
  // приглашение отправлено, по ссылке ещё не переходили
  INVITED = 'INVITED',
  ACTIVE = 'ACTIVE',
  FIRED = 'FIRED'
}

export interface ISellerEmployee {
  _id: string
  seller_id: string
  // аккаунт появляется, только когда приглашение примут
  user_id: string | null
  name: string
  surname: string
  email: string
  permissions: ESellerPermission[]
  status: ESellerEmployeeStatus
  joined_at: string | null
  fired_at: string | null
  invitation_expires_at: string | null
  createdAt: string
}

export type TSellerPanelMe =
  | { role: 'OWNER' }
  | { role: 'EMPLOYEE'; employee: ISellerEmployee }

export interface IGetSellerEmployeesQuery {
  include_fired?: boolean
  seller_id?: string
}

export interface IInviteSellerEmployeeRequest {
  email: string
  permissions: ESellerPermission[]
  seller_id?: string
}

export interface IOpenInvitationResponse {
  shop_name: string
  email: string
  // аккаунта с почтой приглашения нет или в нём не указано имя
  needs_name: boolean
  // у аккаунта с почтой приглашения ещё нет пароля
  needs_password: boolean
  // телефона в аккаунте нет или он не подтверждён
  needs_phone: boolean
  // подтверждённый номер аккаунта для сверки; null — номер спросим
  phone: string | null
  // аккаунт ещё не давал согласия на обработку ПД
  needs_consent: boolean
}

export type TInvitationErrorCode =
  | 'INVITATION_NOT_FOUND'
  | 'INVITATION_EXPIRED'
  | 'INVITATION_OUTDATED'

export interface IInvitationPhoneCodeResponse {
  // номер со скрытыми цифрами, кроме двух последних
  phone: string
  resend_after_sec: number
}

export interface IAcceptInvitationRequest {
  token: string
  name?: string
  surname?: string
  password?: string
  password_confirm?: string
  code?: string
  personal_data_consent?: boolean
}

export interface IAcceptInvitationResponse extends ITokensResponse {
  user_id: string
  isSeller: boolean
}

export interface IUpdateSellerEmployeeRequest {
  name?: string
  surname?: string
  permissions?: ESellerPermission[]
}

// Владелец магазина первой строкой в списке сотрудников
export interface ISellerOwner {
  user_id: string | null
  name: string
  surname: string
  email: string
  // когда заведён магазин
  since: string | null
}

// Коды передачи владения уходят на телефон и почту владельца, контакты — со скрытыми символами
export interface IOwnershipCodesResponse {
  phone: string
  email: string
  resend_after_sec: number
}

export interface IConfirmOwnershipTransferRequest {
  sms_code: string
  email_code: string
}

// Разделы профиля, которые сотрудник правит только по коду из SMS владельцу
export type TEmployeeAccessSection = 'BANKING' | 'SHOP'

// until — ISO-время, до которого раздел открыт; null — закрыт
export type TEmployeeAccess = Record<TEmployeeAccessSection, string | null>

// Профиль магазина от сотрудника: раздел уходит, только пока открыт по коду, —
// любое поле закрытого раздела бэкенд отбивает 403
export interface IEmployeeUpdateSellerRequest {
  seller_id: string
  shop_details?: { shop_name: string; desc: string }
  default_tax?: number
  // File — новое изображение, строка — ключ прежнего
  logo?: File | string
  banner?: File | string
  banking_details?: { name: string; BIK: string; PC: string; KC: string }
  card_number?: string
}

export interface IRequestSectionAccessResponse {
  // номер владельца со скрытыми цифрами, кроме двух последних
  phone: string
  resend_after_sec: number
}

export interface IConfirmSectionAccessResponse {
  section: TEmployeeAccessSection
  until: string
}

export type TNotificationTopic = keyof ISellerNotificationTopics
// О сообщениях покупателей приходит только пуш, о сообщениях поддержки — пуш
// и мессенджер, поэтому галочка support_messages есть лишь у ботов
export type TChatNotificationTopic = 'buyer_messages' | 'support_messages'
export type TNotificationChannel = 'email' | 'sms' | 'telegram' | 'max'
export type TNotificationSettings = Record<
  TNotificationChannel,
  ISellerNotificationTopics & Record<TChatNotificationTopic, boolean>
>

export interface INotificationSettingsTarget {
  seller_id?: string
  // без employee_id — настройки владельца магазина
  employee_id?: string
}

export interface INotificationSettingsResponse {
  target: {
    type: 'OWNER' | 'EMPLOYEE'
    employee_id: string | null
    name: string
    surname: string
  }
  settings: TNotificationSettings
  // темы, которые сотрудник получает по своим разделам; у владельца — все
  available_topics: (TNotificationTopic | TChatNotificationTopic)[]
  contacts: {
    email: string | null
    email_verified: boolean
    phone: number | null
    phone_verified: boolean
    telegram_connected: boolean
    max_connected: boolean
  }
  // ссылки на ботов приходят только для своего аккаунта, админу — null
  bot: {
    bot_api_token: string | null
    max_bot_link: string | null
  } | null
}
